// MoteurConfigManager.jsx - Version adaptée aux couleurs ISS
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { 
    Smartphone, CreditCard, Settings, RefreshCw, 
    CheckCircle2, XCircle, AlertCircle, Save, Edit3
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { APP_CONFIG } from '../../../config/appConfig';

const MoteurConfigManager = () => {
    const { token } = useAuth();
    const [configs, setConfigs] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);

    const moteurs = [
        { id: 'orangeSms', name: 'Orange SMS', icon: Smartphone, color: '#ef7c00' },
        { id: 'mtnSms', name: 'MTN Mobile Money', icon: CreditCard, color: '#009640' },
        { id: 'moovSms', name: 'Moov Network', icon: Smartphone, color: '#009640' }
    ];

    const fetchConfigs = useCallback(async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/moteurs`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 401) throw new Error('Non autorisé');
                if (response.status === 403) throw new Error('Accès refusé');
                throw new Error(`Erreur HTTP ${response.status}`);
            }
            
            const data = await response.json();
            setConfigs(data);
        } catch (err) {
            setError(err.message);
            toast.error(`Erreur: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchConfigs();
    }, [fetchConfigs, token]);

    const handleSave = async (moteurId) => {
        if (!configs[moteurId]) return;
        
        setSaving(true);
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/moteurs/${moteurId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(configs[moteurId])
            });
            
            if (!response.ok) throw new Error(`Erreur ${response.status}`);
            
            toast.success(`Configuration de ${moteurId} sauvegardée`);
            setEditing(null);
        } catch (err) {
            toast.error(`Erreur: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async (moteurId) => {
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/config/moteurs/${moteurId}/test`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error(`Erreur ${response.status}`);
            
            toast.success(`Test ${moteurId} réussi !`);
        } catch (err) {
            toast.error(`Test échoué: ${err.message}`);
        }
    };

    if (!token) {
        return (
            <div className="p-8 text-center font-serif">
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-lg p-4">
                    <AlertCircle size={24} className="text-[#ef7c00] mx-auto mb-2" />
                    <p className="text-[#212529] font-serif">Authentification requise</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 font-serif">
            <Toaster position="top-right" />
            
            {/* En-tête */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[#e9ecef] pb-4">
                <div>
                    <h2 className="text-3xl font-serif font-bold text-[#212529] flex items-center gap-3">
                        <Settings size={28} className="text-[#009640]" />
                        Configuration des Moteurs
                    </h2>
                    <p className="text-sm text-[#6c757d] font-serif">
                        Gérez les paramètres des moteurs de communication
                    </p>
                </div>
                <button 
                    onClick={fetchConfigs} 
                    className="p-3 bg-[#f8f9fa] border border-[#e9ecef] rounded-xl hover:bg-[#e8f5ed] transition-all duration-200"
                    disabled={loading}
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} style={{ color: '#009640' }} />
                </button>
            </div>

            {error && (
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-xl p-4 text-[#212529] text-sm font-serif">
                    <strong>Erreur :</strong> {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {moteurs.map((moteur) => {
                    const config = configs[moteur.id] || {};
                    const isEditing = editing === moteur.id;
                    
                    return (
                        <div key={moteur.id} className="bg-white rounded-2xl border border-[#e9ecef] shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                            {/* En-tête du moteur */}
                            <div className="p-5 border-b border-[#e9ecef] flex justify-between items-center" style={{ borderLeft: `4px solid ${moteur.color}` }}>
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${moteur.color}15` }}>
                                        <moteur.icon size={20} style={{ color: moteur.color }} />
                                    </div>
                                    <div>
                                        <h3 className="font-serif font-bold text-[#212529]">{moteur.name}</h3>
                                        <span className={`text-[10px] font-serif font-bold uppercase tracking-wider ${config.enabled ? 'text-[#009640]' : 'text-[#ef7c00]'}`}>
                                            {config.enabled ? '✓ Actif' : '✗ Inactif'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleTest(moteur.id)}
                                        className="px-3 py-1.5 text-[10px] font-serif font-bold text-[#009640] bg-[#e8f5ed] rounded-lg hover:bg-[#d4edda] transition-colors"
                                    >
                                        Tester
                                    </button>
                                    <button
                                        onClick={() => setEditing(isEditing ? null : moteur.id)}
                                        className="p-1.5 rounded-lg hover:bg-[#f8f9fa] transition-colors"
                                    >
                                        <Edit3 size={16} className="text-[#6c757d]" />
                                    </button>
                                </div>
                            </div>

                            {/* Corps de la configuration */}
                            <div className="p-5 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                            API Key
                                        </label>
                                        <input
                                            type={isEditing ? "text" : "password"}
                                            value={config.apiKey || ''}
                                            onChange={(e) => setConfigs(prev => ({
                                                ...prev,
                                                [moteur.id]: { ...prev[moteur.id], apiKey: e.target.value }
                                            }))}
                                            disabled={!isEditing}
                                            className="w-full px-3 py-2 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-lg disabled:opacity-60 focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-serif font-bold text-[#6c757d] uppercase tracking-wider mb-1.5">
                                            URL
                                        </label>
                                        <input
                                            type="text"
                                            value={config.url || ''}
                                            onChange={(e) => setConfigs(prev => ({
                                                ...prev,
                                                [moteur.id]: { ...prev[moteur.id], url: e.target.value }
                                            }))}
                                            disabled={!isEditing}
                                            className="w-full px-3 py-2 text-sm font-serif bg-[#f8f9fa] border border-[#e9ecef] rounded-lg disabled:opacity-60 focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={config.enabled || false}
                                            onChange={(e) => setConfigs(prev => ({
                                                ...prev,
                                                [moteur.id]: { ...prev[moteur.id], enabled: e.target.checked }
                                            }))}
                                            disabled={!isEditing}
                                            className="w-4 h-4 rounded border-[#e9ecef] text-[#009640] focus:ring-[#009640] focus:ring-2 disabled:opacity-60"
                                        />
                                        <span className="text-xs font-serif text-[#495057]">Activé</span>
                                    </label>
                                    
                                    {isEditing && (
                                        <button
                                            onClick={() => handleSave(moteur.id)}
                                            disabled={saving}
                                            className="ml-auto px-4 py-2 text-xs font-serif font-bold text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-2"
                                            style={{ backgroundColor: '#009640' }}
                                        >
                                            <Save size={14} />
                                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                                        </button>
                                    )}
                                </div>

                                {config.lastTest && (
                                    <div className="text-[10px] font-serif text-[#6c757d] border-t border-[#e9ecef] pt-3 mt-2">
                                        Dernier test: {new Date(config.lastTest).toLocaleString()}
                                        {' '}
                                        {config.lastTestStatus === 'success' ? (
                                            <CheckCircle2 size={12} className="inline text-[#009640]" />
                                        ) : (
                                            <XCircle size={12} className="inline text-[#ef7c00]" />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MoteurConfigManager;